import { useState } from 'react';

interface BusinessDetailsData {
  business_activity: string;
  address: string;
  colonia: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  incorporation_date: string;
  nationality: string;
}

interface BusinessDetailsStepProps {
  initialData?: Partial<BusinessDetailsData>;
  onSubmit: (data: BusinessDetailsData) => void;
  onBack: () => void;
  isSubmitting?: boolean;
}

const inputClass =
  'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

export const BusinessDetailsStep: React.FC<BusinessDetailsStepProps> = ({
  initialData,
  onSubmit,
  onBack,
  isSubmitting = false,
}) => {
  const [form, setForm] = useState<BusinessDetailsData>({
    business_activity: initialData?.business_activity ?? '',
    address: initialData?.address ?? '',
    colonia: initialData?.colonia ?? '',
    city: initialData?.city ?? '',
    state: initialData?.state ?? '',
    zip_code: initialData?.zip_code ?? '',
    country: initialData?.country ?? 'México',
    incorporation_date: initialData?.incorporation_date ?? '',
    nationality: initialData?.nationality ?? 'Mexicana',
  });

  const set = <K extends keyof BusinessDetailsData>(key: K, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-gray-800 mb-4">Actividad</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label htmlFor="businessActivity" className={labelClass}>
              Actividad empresarial *
            </label>
            <textarea
              className={`${inputClass} resize-none`}
              rows={2}
              value={form.business_activity}
              onChange={e => set('business_activity', e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="incorporationDate" className={labelClass}>
              Fecha de constitución *
            </label>
            <input
              className={inputClass}
              type="date"
              value={form.incorporation_date}
              onChange={e => set('incorporation_date', e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="nationality" className={labelClass}>
              Nacionalidad *
            </label>
            <input
              className={inputClass}
              value={form.nationality}
              onChange={e => set('nationality', e.target.value)}
              required
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-800 mb-4">Domicilio fiscal</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label htmlFor="address" className={labelClass}>
              Calle y número *
            </label>
            <input
              className={inputClass}
              value={form.address}
              onChange={e => set('address', e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="colonia" className={labelClass}>
              Colonia *
            </label>
            <input
              className={inputClass}
              value={form.colonia}
              onChange={e => set('colonia', e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="city" className={labelClass}>
              Ciudad *
            </label>
            <input
              className={inputClass}
              value={form.city}
              onChange={e => set('city', e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="state" className={labelClass}>
              Estado *
            </label>
            <input
              className={inputClass}
              value={form.state}
              onChange={e => set('state', e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="zipCode" className={labelClass}>
              Código postal *
            </label>
            <input
              className={inputClass}
              value={form.zip_code}
              onChange={e => set('zip_code', e.target.value)}
              maxLength={5}
              required
            />
          </div>
          <div>
            <label htmlFor="country" className={labelClass}>
              País *
            </label>
            <input
              className={inputClass}
              value={form.country}
              onChange={e => set('country', e.target.value)}
              required
            />
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="px-6 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Anterior
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
        >
          {isSubmitting ? 'Guardando...' : 'Siguiente'}
        </button>
      </div>
    </form>
  );
};
